<template>
  <button class="app-btn" :class="[`app-btn--${variant}`, { 'app-btn--outline': outline, 'app-btn--block': block }]"
    v-bind="$attrs" v-on="$listeners" :type="type" :disabled="disabled">
    <slot></slot>
  </button>
</template>

<script>
export default {
  name: 'AppButton',
  inheritAttrs: false,
  props: {
    type: {
      type: String,
      default: 'button'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    variant: {
      type: String,
      default: 'primary'
    },
    outline: {
      type: Boolean,
      default: false
    },
    block: {
      type: Boolean,
      default: false
    }
  }
}
</script>

<style scoped>
.app-btn--block {
  width: 100%;
}

.app-btn {
  display: inline-block;
  font-weight: 600;
  color: var(--app-text-color, #656565);
  text-align: center;
  vertical-align: middle;
  cursor: pointer;
  background-color: transparent;
  border: 1px solid transparent;
  padding: 0.375rem 0.75rem;
  font-size: var(--app-font-size-base, 1rem);
  line-height: var(--app-line-height, 1.5);
  border-radius: var(--app-border-radius, 0.25rem);
  transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out, filter 0.15s ease-in-out;
}

.app-btn:hover {
  text-decoration: none;
}

.app-btn:focus {
  outline: 0;
  box-shadow: 0 0 0 0.2rem var(--app-primary-focus, rgba(15, 40, 53, 0.25));
}

.app-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

/* Modifiers - Primary */
.app-btn.app-btn--primary,
.app-btn.btn-primary,
.app-btn[variant="primary"] {
  color: #fff;
  background-color: var(--app-primary);
  border-color: var(--app-primary);
}

.app-btn.app-btn--primary:hover:not(:disabled),
.app-btn.btn-primary:hover:not(:disabled),
.app-btn[variant="primary"]:hover:not(:disabled) {
  filter: brightness(0.9);
}

.app-btn.app-btn--primary.app-btn--outline,
.app-btn.btn-outline-primary {
  color: var(--app-primary);
  background-color: transparent;
  border-color: var(--app-primary);
}

.app-btn.app-btn--primary:focus,
.app-btn.btn-primary:focus,
.app-btn[variant="primary"]:focus,
.app-btn.btn-outline-primary:focus {
  box-shadow: 0 0 0 0.2rem var(--app-primary-focus, rgba(15, 40, 53, 0.25));
}

.app-btn.app-btn--primary.app-btn--outline:hover:not(:disabled),
.app-btn.btn-outline-primary:hover:not(:disabled) {
  color: #fff;
  background-color: var(--app-primary);
}

/* Modifiers - Secondary */
.app-btn.app-btn--secondary,
.app-btn.btn-secondary,
.app-btn[variant="secondary"] {
  color: #fff;
  background-color: var(--app-secondary);
  border-color: var(--app-secondary);
}

.app-btn.app-btn--secondary:hover:not(:disabled),
.app-btn.btn-secondary:hover:not(:disabled),
.app-btn[variant="secondary"]:hover:not(:disabled) {
  filter: brightness(0.9);
}

.app-btn.app-btn--secondary.app-btn--outline,
.app-btn.btn-outline-secondary {
  color: var(--app-secondary);
  background-color: transparent;
  border-color: var(--app-secondary);
}

.app-btn.app-btn--secondary:focus,
.app-btn.btn-secondary:focus,
.app-btn[variant="secondary"]:focus,
.app-btn.btn-outline-secondary:focus {
  box-shadow: 0 0 0 0.2rem var(--app-secondary-focus, rgba(19, 71, 92, 0.25));
}

.app-btn.app-btn--secondary.app-btn--outline:hover:not(:disabled),
.app-btn.btn-outline-secondary:hover:not(:disabled) {
  color: #fff;
  background-color: var(--app-secondary);
}

/* Modifiers - Danger */
.app-btn.app-btn--danger,
.app-btn.btn-danger,
.app-btn[variant="danger"] {
  color: #fff;
  background-color: var(--app-danger);
  border-color: var(--app-danger);
}

.app-btn.app-btn--danger:hover:not(:disabled),
.app-btn.btn-danger:hover:not(:disabled),
.app-btn[variant="danger"]:hover:not(:disabled) {
  filter: brightness(0.9);
}

.app-btn.app-btn--danger.app-btn--outline,
.app-btn.btn-outline-danger {
  color: var(--app-danger);
  background-color: transparent;
  border-color: var(--app-danger);
}

.app-btn.app-btn--danger:focus,
.app-btn.btn-danger:focus,
.app-btn[variant="danger"]:focus,
.app-btn.btn-outline-danger:focus {
  box-shadow: 0 0 0 0.2rem var(--app-danger-focus, rgba(244, 106, 106, 0.25));
}

.app-btn.app-btn--danger.app-btn--outline:hover:not(:disabled),
.app-btn.btn-outline-danger:hover:not(:disabled) {
  color: #fff;
  background-color: var(--app-danger);
}

/* Modifiers - Success */
.app-btn.app-btn--success,
.app-btn.btn-success,
.app-btn[variant="success"] {
  color: #fff;
  background-color: var(--app-success);
  border-color: var(--app-success);
}

.app-btn.app-btn--success:hover:not(:disabled),
.app-btn.btn-success:hover:not(:disabled),
.app-btn[variant="success"]:hover:not(:disabled) {
  filter: brightness(0.9);
}

.app-btn.app-btn--success.app-btn--outline,
.app-btn.btn-outline-success {
  color: var(--app-success);
  background-color: transparent;
  border-color: var(--app-success);
}

.app-btn.app-btn--success:focus,
.app-btn.btn-success:focus,
.app-btn[variant="success"]:focus,
.app-btn.btn-outline-success:focus {
  box-shadow: 0 0 0 0.2rem var(--app-success-focus, rgba(52, 195, 143, 0.25));
}

.app-btn.app-btn--success.app-btn--outline:hover:not(:disabled),
.app-btn.btn-outline-success:hover:not(:disabled) {
  color: #fff;
  background-color: var(--app-success);
}
</style>
